import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import {
  checkAuthStatus,
  getCurrentAuthUser,
  signInUser,
  signOutUser,
  getAuthTokens,
  refreshAuthTokens,
  areTokensExpired,
  AuthUser,
  AuthTokens,
  AuthRetryState,
} from "../utils/auth";
import { parseAuthError, AuthError } from "../utils/authErrors";

interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  tokens: AuthTokens | null;
  isLoading: boolean;
  error: AuthError | null;
  retryState: AuthRetryState | null;
}

interface AuthContextType extends AuthState {
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshTokens: () => Promise<void>;
  clearError: () => void;
  retryLastOperation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    tokens: null,
    isLoading: true,
    error: null,
    retryState: null,
  });

  // Store last operation for retry functionality
  const [lastOperation, setLastOperation] = useState<{
    type: "signIn" | "refreshTokens";
    params?: any;
  } | null>(null);

  const handleSessionExpired = async () => {
    console.log("Session expired, signing out user");
    const sessionError: AuthError = {
      type: "session",
      message: "Your session has expired. Please sign in again.",
      retryable: false,
    };

    setAuthState({
      isAuthenticated: false,
      user: null,
      tokens: null,
      isLoading: false,
      error: sessionError,
      retryState: null,
    });

    try {
      await signOutUser();
    } catch (error) {
      console.error("Error during session cleanup:", error);
    }

    // Clear last operation since session is expired
    setLastOperation(null);

    // Redirect to login page
    window.location.href = "/login";
  };

  const handleTokenRefresh = useCallback(async () => {
    try {
      setLastOperation({ type: "refreshTokens" });
      const newTokens = await refreshAuthTokens();
      if (newTokens) {
        setAuthState((prev) => ({
          ...prev,
          tokens: newTokens,
          error: null,
          retryState: null,
        }));
        console.log("Tokens refreshed successfully");
      } else {
        console.log("Token refresh failed, signing out user");
        await handleSessionExpired();
      }
    } catch (error) {
      console.error("Token refresh error:", error);
      const authError = parseAuthError(error);

      if (authError.type === "session") {
        await handleSessionExpired();
      } else {
        setAuthState((prev) => ({
          ...prev,
          error: authError,
          retryState: {
            attemptNumber: 1,
            lastError: authError,
            isRetrying: false,
          },
        }));
      }
    }
  }, []);

  // Check authentication status on mount
  useEffect(() => {
    checkInitialAuthStatus();
  }, []);

  // Set up token refresh interval
  useEffect(() => {
    let refreshInterval: NodeJS.Timeout;

    if (authState.isAuthenticated && authState.tokens) {
      // Check token expiration every minute
      refreshInterval = setInterval(async () => {
        if (areTokensExpired(authState.tokens)) {
          console.log("Tokens expired, attempting refresh...");
          await handleTokenRefresh();
        }
      }, 60 * 1000); // Check every minute
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [authState.isAuthenticated, authState.tokens, handleTokenRefresh]);

  const checkInitialAuthStatus = async () => {
    try {
      setAuthState((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
        retryState: null,
      }));

      const isAuth = await checkAuthStatus();
      if (isAuth) {
        const [user, tokens] = await Promise.all([
          getCurrentAuthUser(),
          getAuthTokens(),
        ]);

        setAuthState({
          isAuthenticated: true,
          user,
          tokens,
          isLoading: false,
          error: null,
          retryState: null,
        });
      } else {
        setAuthState({
          isAuthenticated: false,
          user: null,
          tokens: null,
          isLoading: false,
          error: null,
          retryState: null,
        });
      }
    } catch (error) {
      console.error("Error checking auth status:", error);
      const authError = parseAuthError(error);
      setAuthState({
        isAuthenticated: false,
        user: null,
        tokens: null,
        isLoading: false,
        error: authError,
        retryState: null,
      });
    }
  };

  const signIn = async (username: string, password: string): Promise<void> => {
    try {
      setAuthState((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
        retryState: null,
      }));

      // Store operation for potential retry
      setLastOperation({ type: "signIn", params: { username, password } });

      await signInUser(username, password);

      // Get user info and tokens after successful sign in
      const [user, tokens] = await Promise.all([
        getCurrentAuthUser(),
        getAuthTokens(),
      ]);

      setAuthState({
        isAuthenticated: true,
        user,
        tokens,
        isLoading: false,
        error: null,
        retryState: null,
      });

      // Clear last operation on success
      setLastOperation(null);
    } catch (error) {
      console.error("Sign in error:", error);

      // Extract auth error information if available
      const authError = (error as any).authError || parseAuthError(error);
      const attemptNumber = (error as any).attemptNumber || 1;

      setAuthState((prev) => ({
        ...prev,
        isAuthenticated: false,
        user: null,
        tokens: null,
        isLoading: false,
        error: authError,
        retryState: authError.retryable
          ? {
              attemptNumber,
              lastError: authError,
              isRetrying: false,
            }
          : null,
      }));
      throw error;
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      setAuthState((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
        retryState: null,
      }));

      await signOutUser();

      setAuthState({
        isAuthenticated: false,
        user: null,
        tokens: null,
        isLoading: false,
        error: null,
        retryState: null,
      });

      // Clear last operation on sign out
      setLastOperation(null);
    } catch (error) {
      console.error("Sign out error:", error);
      const authError = parseAuthError(error);

      // Even if sign out fails, clear local state
      setAuthState({
        isAuthenticated: false,
        user: null,
        tokens: null,
        isLoading: false,
        error: {
          ...authError,
          message: "Sign out failed, but local session cleared",
        },
        retryState: null,
      });

      // Clear last operation even on failure
      setLastOperation(null);
    }
  };

  const refreshTokens = async (): Promise<void> => {
    try {
      const newTokens = await refreshAuthTokens();
      if (newTokens) {
        setAuthState((prev) => ({
          ...prev,
          tokens: newTokens,
          error: null,
          retryState: null,
        }));
      } else {
        // If we can't refresh tokens, check if user is still authenticated
        const isAuth = await checkAuthStatus();
        if (!isAuth) {
          await handleSessionExpired();
        }
      }
    } catch (error) {
      console.error("Token refresh error:", error);
      const authError = parseAuthError(error);

      if (authError.type === "session") {
        await handleSessionExpired();
      } else {
        // If token refresh fails, check auth status
        const isAuth = await checkAuthStatus();
        if (!isAuth) {
          await handleSessionExpired();
        } else {
          setAuthState((prev) => ({
            ...prev,
            error: authError,
            retryState: authError.retryable
              ? {
                  attemptNumber: 1,
                  lastError: authError,
                  isRetrying: false,
                }
              : null,
          }));
        }
      }
    }
  };

  const clearError = () => {
    setAuthState((prev) => ({ ...prev, error: null, retryState: null }));
  };

  const retryLastOperation = async (): Promise<void> => {
    if (!lastOperation || !authState.retryState) {
      return;
    }

    setAuthState((prev) => ({
      ...prev,
      retryState: prev.retryState
        ? {
            ...prev.retryState,
            isRetrying: true,
          }
        : null,
    }));

    try {
      if (lastOperation.type === "signIn") {
        const { username, password } = lastOperation.params;
        await signIn(username, password);
      } else if (lastOperation.type === "refreshTokens") {
        await refreshTokens();
      }
    } catch (error) {
      // Error handling is done in the individual methods
      console.error("Retry operation failed:", error);
    }
  };

  const contextValue: AuthContextType = {
    ...authState,
    signIn,
    signOut,
    refreshTokens,
    clearError,
    retryLastOperation,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
